import type { FC, MouseEvent } from 'react';

import { isNextAuthEnabled } from '@eneco/auth';
import { LoginButton } from '@eneco/components-next/auth';
import { useLinkComponent } from '@eneco/link';
import { NavLink } from '@eneco/sparky';
import { ProfileIcon } from '@eneco/sparky/icons';
import { LoginMenuRendering } from '@sitecore/types/LoginMenu';
import Cookies from 'js-cookie';

import usePlaceholderComponentContent from './usePlaceholderComponentContent';

const CUSTOMER_INSIGHTS_VISITOR_ID_COOKIE = '_msci';
const CUSTOMER_INSIGHTS_SEARCH_PARAM = 'visitor_id';

const LoginMenu: FC = function () {
  const Link = useLinkComponent();
  const content = usePlaceholderComponentContent<LoginMenuRendering>('jss-main-navigation-right', 'LoginMenu');
  if (!content) return null;

  const { fields } = content;

  if (!isNextAuthEnabled) {
    const selfServiceHref = fields.selfServiceLink.value.href;
    const sendVisitorId = fields.sendCustomerInsightsVisitorIdFlag?.value;

    const handleClick = (e: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
      if (!sendVisitorId) return;

      // In order to match unauthorised visitor behaviour with a customer profile,
      // we send an anonymous visitor ID as a search param to the self-service portal.
      let url: URL;
      try {
        url = new URL(selfServiceHref);
      } catch {
        return; // relative or invalid href — fall through to default navigation
      }

      const customerInsightsVisitorId = Cookies.get(CUSTOMER_INSIGHTS_VISITOR_ID_COOKIE);
      if (customerInsightsVisitorId) {
        url.searchParams.set(CUSTOMER_INSIGHTS_SEARCH_PARAM, customerInsightsVisitorId);
      }

      // Update the anchor href so all clicks (plain, Ctrl/Cmd, middle, shift)
      // carry the visitor_id. The browser handles navigation via this href — no override needed.
      (e.currentTarget as HTMLAnchorElement).href = url.toString();
    };

    return (
      <Link href={selfServiceHref} linkType={fields.selfServiceLink.value.linktype}>
        <NavLink leftIcon={<ProfileIcon />} onClick={handleClick}>
          {fields.selfServiceLink.value.text}
        </NavLink>
      </Link>
    );
  }

  return (
    <LoginButton
      leftIcon={<ProfileIcon />}
      label={fields.selfServiceLink.value.text}
      callbackUrl={fields.selfServiceLink.value.href}
      sendLocale={!!fields.sendLocaleToOkta?.value}
    />
  );
};

export default LoginMenu;
